import type { ClientProjectListRow } from '@/types/client-project'
import type { MarketProjectListRow } from '@/types/market-project'
import type { FreelancerMarketProjectListRow } from '@/types/freelancer-market-project'
import type { ProjectCardModel } from '@/types/project-card-model'

/**
 * 기업 내 프로젝트 RPC row → ProjectCardModel
 */
export const mapClientProjectRowToCard = (row: ClientProjectListRow, currentUserId?: string | null): ProjectCardModel => {
  const mapped = {
    id: row.counsel_id,
    title: row.title ?? '제목 없음',
    cost: row.cost,
    period: row.period ?? '',
    workType: row.work_type ?? null,
    fieldLabel: row.field?.[0] ?? '분야 미지정', // 첫 번째 값 (하위 호환성)
    feilds: row.field ?? null, // feild 배열 전체
    skills: row.skill ?? [],
    createdAt: row.created_at,
    startDate: row.start_date ?? null,
    dueDate: row.due_date ?? null,
    // 프로젝트 설명: outline 컬럼만 사용
    description: row.outline ?? null,
  
  // 기업 내 프로젝트는 견적 정보 포함
  // 문서 기준: SUBMITTED/ACCEPTED만 count (DRAFT 제외)
  // est_submitted가 있으면 우선 사용, 없으면 est_visible 사용 (하위 호환성)
  estimateCount: (row.est_submitted ?? row.est_visible ?? 0) > 0 
    ? (row.est_submitted ?? row.est_visible ?? 0)
    : 0,
  estimateStatus: (
    (row.est_accepted ?? 0) > 0 ? 'accepted'
    : ((row.est_submitted ?? row.est_visible ?? 0) > 0) ? 'pending'
    : 'none'
  ) as 'accepted' | 'pending' | 'none',
  
  // 북마크는 내 프로젝트에서는 사용 안 함
  isBookmarked: row.is_bookmarked,
  
  // 소유자 판단 (RPC가 내 프로젝트만 반환하므로 항상 true)
  ownerClientId: currentUserId ?? undefined,
  
    // 프로젝트 상태
    counselStatus: row.counsel_status as 'pending' | 'matched' | 'end' | undefined,
  }

  return mapped
}

/**
 * 마켓 프로젝트 RPC row → ProjectCardModel
 */
export const mapMarketProjectRowToCard = (row: MarketProjectListRow, currentUserId?: string | null): ProjectCardModel => ({
  id: row.counsel_id,
  title: row.title ?? '제목 없음',
  cost: row.cost,
  period: row.period ?? '',
  workType: row.work_type ?? null,
  fieldLabel: row.field?.[0] ?? '분야 미지정',
  feilds: row.field ?? null,
  skills: row.skill ?? [],
  createdAt: row.created_at,
  startDate: row.start_date ?? null,
  description: row.outline ?? null,
  
  // 마켓에서는 견적 정보 제외 (필요하면 추가 가능)
  
  // 북마크 정보 포함
  isBookmarked: row.is_bookmarked,
  
  // 소유자 판단 (마켓에서는 client_id로 확인)
  ownerClientId: row.client_id ?? undefined,
})

/**
 * 프리랜서용 마켓 프로젝트 RPC row → ProjectCardModel
 * (내가 제출한 견적 상태 포함)
 */
export const mapFreelancerMarketProjectRowToCard = (
  row: FreelancerMarketProjectListRow, 
  currentUserId?: string | null
): ProjectCardModel => {
  // 제목 null 방어: 빈 문자열이나 null이면 분야 기반 기본 제목 생성
  let title = row.title?.trim() || '';
  if (!title || title.length === 0) {
    const firstField = row.field?.[0];
    if (firstField) {
      title = `[${firstField}] 프로젝트`;
    } else {
      title = '제목 미정';
    }
  }
  
  return {
    id: row.counsel_id,
    title,
    cost: row.cost,
    period: row.period ?? '',
    workType: row.work_type ?? null,
    fieldLabel: row.field?.[0] ?? '분야 미지정', // 첫 번째 값 (하위 호환성)
    feilds: row.field ?? null, // feild 배열 전체
    skills: row.skill ?? [],
    createdAt: row.created_at,
    startDate: row.start_date ?? null,
    // 프로젝트 설명: outline 컬럼만 사용
    description: row.outline ?? null,
    
    // 북마크 정보 포함
    isBookmarked: row.is_bookmarked,
    
    // 소유자 판단
    ownerClientId: row.client_id ?? undefined,
    
    // 프리랜서 전용: 내가 제출한 견적 정보
    hasSubmittedEstimate: row.has_submitted,
    myEstimateStatus: row.my_estimate_status ?? undefined,
    myEstimateId: row.my_estimate_id ?? undefined,
    
    // 프로젝트 상태
    counselStatus: row.counsel_status,
  };
}

